import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { Role } from '@lms/shared-types'
import { roleAtLeast } from '@lms/shared-types'
import { hasPrimaryShortcutModifier } from './platform'

/**
 * Global keyboard shortcut map.
 *
 * Single `keydown` listener on window that handles:
 *   - `<mod> + /`       → open shortcuts help modal
 *   - `Escape`          → close shortcuts help modal
 *   - `<mod> + <letter>`→ navigation chords (see switch below)
 *
 * `<mod>` is the primary shortcut modifier — ⌥ (Option) on macOS, Alt on
 * Windows / Linux. Both are the same physical key (`event.altKey`), so the
 * matching is platform-independent (see `hasPrimaryShortcutModifier`). We
 * avoid ⌘ because ⌘+letter collides with reserved macOS shortcuts.
 *
 * Why a modifier instead of bare keys: bare single-key shortcuts (the old
 * `?` and `g`-leader sequences) fired on stray keystrokes while reading a
 * page, yanking the user to another route. Requiring a modifier makes every
 * shortcut deliberate.
 *
 * Matching:
 *   - We match on `event.code` (`KeyH`, `Slash`), NOT `event.key`. On macOS,
 *     Option composes accented characters into `event.key`, and even ⌘ chords
 *     are clearer to match by physical key. `event.code` is stable across
 *     both modifiers and platforms.
 *
 * Guards:
 *   - The platform modifier is required; the wrong-platform modifier is a
 *     no-op (Alt on macOS, ⌘ / Ctrl on Windows).
 *   - No-op when focus is inside input / textarea / contenteditable.
 *
 * Returns `[helpOpen, setHelpOpen]` so AppShell can render the modal.
 */
export function useGlobalKeymap(role: Role): [boolean, (open: boolean) => void] {
  const [helpOpen, setHelpOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      // Escape closes the help modal — no modifier required. (The Modal
      // primitive handles its own Escape, but we also close here so the hook
      // state stays in sync.)
      if (event.key === 'Escape') {
        if (helpOpen) {
          event.preventDefault()
          setHelpOpen(false)
        }
        return
      }

      // Everything below is a modifier-chord. Require the platform's primary
      // modifier (⌘ on macOS, Alt elsewhere) and no other reserved modifier.
      if (!hasPrimaryShortcutModifier(event)) return

      // Skip when focus is in a text input or editable element.
      const target = event.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName.toLowerCase()
        if (tag === 'input' || tag === 'textarea') return
        if (target.isContentEditable) return
      }

      // `<mod> + /` opens the help modal.
      if (event.code === 'Slash') {
        event.preventDefault()
        setHelpOpen(true)
        return
      }

      // `<mod> + <letter>` navigation chords.
      switch (event.code) {
        case 'KeyH':
          event.preventDefault()
          void navigate({ to: '/' })
          break
        case 'KeyC':
          event.preventDefault()
          void navigate({ to: '/calendar' })
          break
        case 'KeyA':
          event.preventDefault()
          void navigate({ to: '/apply' })
          break
        case 'KeyR':
          if (roleAtLeast(role, 'manager')) {
            event.preventDefault()
            void navigate({ to: '/manager/requests' })
          }
          break
        case 'KeyD':
          if (roleAtLeast(role, 'manager')) {
            event.preventDefault()
            void navigate({ to: '/manager' })
          }
          break
        case 'KeyE':
          if (roleAtLeast(role, 'hr')) {
            event.preventDefault()
            void navigate({ to: '/hr/employees' })
          }
          break
        default:
          // Unknown chord — silently ignore.
          break
      }
    }

    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
    }
    // helpOpen is a dependency because the Escape branch reads it.
  }, [helpOpen, navigate, role])

  return [helpOpen, setHelpOpen]
}
